import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import { message } from "antd";
import {
  FIRESTORE_DEFAULT_PROJECTIONS_DOC,
  FIRESTORE_PRODUCTS_STOCK_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "constants/CompanyConstants";

// Create context
const UseStock = createContext();

// Provider component
export const StockDataProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [stockDates, setStockDates] = useState([]);
  const [stockData, setStockData] = useState({});
  const [stockCarryOverData, setStockCarryOverData] = useState({});
  const [defaultProjections, setDefaultProjections] = useState({});
  const [listeners, setListeners] = useState([]);

  // Set up listeners when the provider mounts
  useEffect(() => {
    setupListeners();

    // Cleanup listeners when unmounting
    return () => {
      listeners.forEach((listener) => listener());
    };
  }, []);

  const setupListeners = () => {
    setIsLoading(true);
    const newListeners = [];

    // Calculate date range: 28 days in the past to 28 days in the future
    const startDate = dayjs().subtract(28, "days").startOf("day").unix();
    const endDate = dayjs().add(28, "days").endOf("day").unix();

    // Listen for stock dates and data with date filtering
    const stockQuery = query(
      collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCTS_STOCK_TABLE),
      where("dateUnix", ">=", startDate),
      where("dateUnix", "<=", endDate),
      orderBy("dateUnix", "asc"),
    );
    const stockListener = onSnapshot(
      stockQuery,
      (snapshot) => {
        const dates = [];
        const stockByDate = {};

        snapshot.docs.forEach((doc) => {
          if (doc.id === FIRESTORE_DEFAULT_PROJECTIONS_DOC) {
            // Process default projections document
            setDefaultProjections(doc.data());
          } else {
            // Process regular stock documents
            const data = doc.data();
            dates.push({
              id: doc.id,
              date: data.date,
              dateUnix: data.dateUnix,
              formattedDate: dayjs(data.dateUnix * 1000).format("DD-MM-YYYY"),
            });
            stockByDate[doc.id] = data.products || {};
            // Also store carry-over status for each date
            if (data.stockCarriedOver !== undefined) {
              setStockCarryOverData((prev) => ({
                ...prev,
                [doc.id]: data.stockCarriedOver,
              }));
            }
          }
        });

        setStockDates(dates);
        setStockData(stockByDate);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error listening to stock data:", error);
        message.error("Failed to fetch stock data");
        setIsLoading(false);
      },
    );

    newListeners.push(stockListener);
    setListeners(newListeners);
  };

  // Get product projections by product ID
  const getProductProjections = (productId) => {
    if (!defaultProjections || !defaultProjections.products) return Array(7).fill(0);

    const productData = defaultProjections.products.find((p) => p.id === productId);
    return productData ? productData.projections : Array(7).fill(0);
  };

  // Get stock for a specific product
  const getProductStock = (productId) => {
    const result = {};

    stockDates.forEach((date) => {
      result[date.id] = stockData[date.id]?.[productId] || {
        projectedStock: 0,
        countedStock: 0,
        requestedStock: 0,
        currentStock: 0,
      };
    });

    return result;
  };

  // Check if stock has been carried over for a specific date
  const getStockCarriedOverStatus = (dateId) => {
    return stockCarryOverData[dateId] === true;
  };

  // Get carry-over status for today
  const getTodayCarryOverStatus = () => {
    const todayDateId = dayjs().format("DDMMYYYY");
    return getStockCarriedOverStatus(todayDateId);
  };

  return (
    <UseStock.Provider
      value={{
        isLoading,
        stockDates,
        stockData,
        stockCarryOverData,
        defaultProjections,
        getProductProjections,
        getProductStock,
        getStockCarriedOverStatus,
        getTodayCarryOverStatus,
      }}
    >
      {children}
    </UseStock.Provider>
  );
};

export const useStockData = () => useContext(UseStock);
